/**
 * The close cockpit — data access (design C3 §4, §6.2; FSD 2.2.8).
 *
 * Two disciplines this file exists to hold on to.
 *
 * **Readiness is computed, never reported** (§4.1). Nothing here derives a
 * task's state: `status` arrives from `Refresh`'s fixpoint and the screens
 * render it. A browser that decided a task looked ready because its
 * predecessors looked done would be a second, disagreeing engine — and the
 * whole value of the cockpit is that it is the one place a controller can
 * trust. The only local computation is `taskStatusTone`, which chooses a colour,
 * and `unsatisfiedNames`, which resolves codes the server already sent into
 * names.
 *
 * **Money is a server-computed string.** A readiness amount is a suspense
 * balance or a sub-ledger difference and is passed to `EMoney` untouched.
 * Nothing in this module parses it.
 *
 * ---------------------------------------------------------------------------
 * The wire, and the shapes the screens read
 * ---------------------------------------------------------------------------
 *
 * Every call goes through the generated `@erpx/api` client. FT3-UB repointed
 * this file off `@/api/c3-contract`, a contract transcribed from the design
 * before `internal/close` existed — and it had drifted: it promised fields the
 * server never sends (`obligationItems`, `canCertify`, `blockedByTaskIds`,
 * company and period names) and request bodies without the `rowVersion` every
 * close write requires. So each response is normalised here, once, from what
 * the server actually sends:
 *
 *  - the enum-valued strings are narrowed to their CHECK-constraint unions;
 *  - optional strings default to `''`, optional lists to `[]`;
 *  - `periodName` is the run's `fiscalPeriodName`, and `companyName` carries the
 *    company **code** — the only company label the close DTOs carry;
 *  - a readiness task's answer is rebuilt as a readiness item from the task's
 *    own `readiness*` fields, and an obligation task's outstanding documents and
 *    counts are read from `computedDetail` (`total`, `satisfied`,
 *    `outstanding`), which is where `close.EvaluateObligation` records them.
 *
 * Nothing is invented: a field the server does not send is not given a value
 * here, it is simply not on the type.
 */

import {
  closeBoard,
  closeCalendarsCreate,
  closeCalendarsGet,
  closeCalendarsList,
  closeCalendarsSeedDefaults,
  closeCalendarsSetTemplates,
  closeCalendarsUpdate,
  closeEvidenceList,
  closeReadiness,
  closeRunsAbandon,
  closeRunsAddTask,
  closeRunsBuildEvidencePack,
  closeRunsCertify,
  closeRunsGet,
  closeRunsList,
  closeRunsOpen,
  closeRunsRefresh,
  closeTasksAccept,
  closeTasksAssign,
  closeTasksComplete,
  closeTasksList,
  closeTasksReopen,
  closeTasksRunAction,
  type AbandonCloseRunRequest,
  type AcceptCloseTaskRequest,
  type AddCloseTaskRequest,
  type AssignCloseTaskRequest,
  type CloseBoardRowDTO as WireBoardRow,
  type CloseCalendarDTO as WireCalendar,
  type CloseCalendarDetailDTO as WireCalendarDetail,
  type CloseEvidenceDTO,
  type CloseEvidencePackDTO,
  type CloseReadinessDTO as WireReadiness,
  type CloseReadinessItemDTO as WireReadinessItem,
  type CloseRunDTO as WireRun,
  type CloseRunDetailDTO as WireRunDetail,
  type CloseSignoffDTO,
  type CloseTaskDTO as WireTask,
  type CloseTaskTemplateDTO as WireTemplate,
  type CloseTemplateInput,
  type CompleteCloseTaskRequest,
  type CreateCloseCalendarRequest,
  type ListCloseRunsQuery,
  type OpenCloseRunRequest,
  type PageCloseEvidenceDTO,
  type ReopenCloseTaskRequest,
  type UpdateCloseCalendarRequest,
} from '@erpx/api'
import { toProblemDetails } from '@erpx/ui'

import { api, rawRequest } from '@/api/client'
import { MAX_LIMIT } from '@/composables/useTableUrlState'

export type {
  AbandonCloseRunRequest,
  AcceptCloseTaskRequest,
  AddCloseTaskRequest,
  AssignCloseTaskRequest,
  CloseEvidenceDTO,
  CloseEvidencePackDTO,
  CloseSignoffDTO,
  CompleteCloseTaskRequest,
  CreateCloseCalendarRequest,
  OpenCloseRunRequest,
  ReopenCloseTaskRequest,
  UpdateCloseCalendarRequest,
}

export const CLOSE_KEYS = {
  calendars: ['close', 'calendars'] as const,
  calendarsPage: (params: Record<string, unknown>) => ['close', 'calendars', 'list', params] as const,
  calendar: (id: number) => ['close', 'calendars', 'one', id] as const,

  runs: ['close', 'runs'] as const,
  runsPage: (params: Record<string, unknown>) => ['close', 'runs', 'list', params] as const,
  run: (id: number) => ['close', 'runs', 'one', id] as const,
  tasks: (runId: number, params: Record<string, unknown>) =>
    ['close', 'runs', runId, 'tasks', params] as const,
  evidence: (runId: number) => ['close', 'runs', runId, 'evidence'] as const,

  board: (params: Record<string, unknown>) => ['close', 'board', params] as const,
  readiness: (companyId: number, periodId: number) =>
    ['close', 'readiness', companyId, periodId] as const,
} as const

/* ------------------------------------------------------------------ enums */

/** `close_calendars.scope` — `ccl_scope_check`. */
export type CloseScope = 'monthly' | 'quarterly' | 'annual'

/** `close_tasks.task_kind` — `ctt_kind_check`. */
export type CloseTaskKind = 'manual' | 'readiness' | 'obligation' | 'action'

/** `close_tasks.status` — `ctk_status_check`, all eight, in the constraint's order. */
export type CloseTaskStatus =
  | 'pending'
  | 'blocked'
  | 'ready'
  | 'in_progress'
  | 'completed'
  | 'accepted'
  | 'skipped'
  | 'failed'

/** `close_runs.status` — `crr_status_check`. */
export type CloseRunStatus =
  | 'not_started'
  | 'in_progress'
  | 'ready'
  | 'certified'
  | 'closed'
  | 'abandoned'

/** `close.Severity` (§4.2). A blocking provider is also what refuses the period close. */
export type CloseSeverity = 'blocking' | 'advisory'

/**
 * All eight task states, in the order `ctk_status_check` declares them.
 *
 * The cockpit renders every one. Six arrive without a human acting: `pending`,
 * `blocked` and `ready` come out of `Refresh`'s fixpoint, `failed` from a
 * provider that errored, `skipped` from a provider whose module the plan does
 * not entitle. Only `completed` and `accepted` are somebody's decision — and
 * `accepted` carries a governed reason precisely so the decision is legible.
 */
export const TASK_STATUSES = [
  'pending',
  'blocked',
  'ready',
  'in_progress',
  'completed',
  'accepted',
  'skipped',
  'failed',
] as const satisfies readonly CloseTaskStatus[]

/** §4.3(2): what counts as satisfied for a dependant. `failed` does not. */
export const SATISFIED_STATUSES: readonly CloseTaskStatus[] = ['completed', 'accepted', 'skipped']

export const RUN_STATUSES = [
  'not_started',
  'in_progress',
  'ready',
  'certified',
  'closed',
  'abandoned',
] as const satisfies readonly CloseRunStatus[]

export const TASK_KINDS = ['manual', 'readiness', 'obligation', 'action'] as const satisfies readonly CloseTaskKind[]

export const CLOSE_SCOPES = ['monthly', 'quarterly', 'annual'] as const satisfies readonly CloseScope[]

/** Statuses a run still accepts writes in; a certified, closed or abandoned run is read-only. */
export const EDITABLE_RUN_STATUSES: readonly CloseRunStatus[] = ['not_started', 'in_progress', 'ready']

/* --------------------------------------------------------- screen shapes */

/** One provider's answer (§4.2 `Readiness`), with the optional strings defaulted. */
export interface CloseReadinessItemDTO
  extends Omit<WireReadinessItem, 'module' | 'severity' | 'messageKey' | 'link' | 'detail'> {
  module: string
  severity: CloseSeverity
  /** An i18n key, never rendered prose. `''` when the provider sent none. */
  messageKey: string
  /** An in-app path to the list that clears the item. `''` when there is none. */
  link: string
  detail: Record<string, unknown>
}

export interface CloseReadinessDTO extends Omit<WireReadiness, 'items'> {
  items: CloseReadinessItemDTO[]
  /** `fiscalPeriodName`, or `''`. */
  periodName: string
  /** When the providers were evaluated — the server's `generatedAt`. */
  evaluatedAt: string
}

/** One outstanding document an obligation task counts, from `computedDetail.outstanding`. */
export interface CloseObligationItemDTO {
  docType: string
  docId: number
  docNo: string
  status: string
  /** Always false: the server records only the outstanding ones individually. */
  satisfied: boolean
  dueOn?: string
  link: string
}

type WireTaskDefaults =
  | 'status'
  | 'taskKind'
  | 'module'
  | 'readinessKey'
  | 'actionKey'
  | 'obligationKind'
  | 'blockedBy'
  | 'dependsOn'
  | 'acceptedReasonCode'
  | 'acceptedNote'
  | 'resultDocType'
  | 'note'
  | 'computedDetail'

export interface CloseTaskDTO extends Omit<WireTask, WireTaskDefaults> {
  status: CloseTaskStatus
  taskKind: CloseTaskKind
  module: string
  readinessKey: string
  actionKey: string
  obligationKind: string
  /** Codes of the unsatisfied predecessors. */
  blockedBy: string[]
  /** Codes of every predecessor. */
  dependsOn: string[]
  acceptedReasonCode: string
  acceptedNote: string
  resultDocType: string
  note: string
  computedDetail: Record<string, unknown>
  /** A readiness task's answer as a readiness item; null for every other kind, and before the first refresh. */
  readiness: CloseReadinessItemDTO | null
  /** An obligation task's outstanding documents. Empty for every other kind. */
  obligationItems: CloseObligationItemDTO[]
  /** An obligation task's population and how much of it is satisfied; 0 when not computed. */
  obligationTotal: number
  obligationSatisfied: number
}

export interface CloseRunDTO
  extends Omit<WireRun, 'status' | 'calendarCode' | 'evidenceHash' | 'certificationStatement' | 'note'> {
  status: CloseRunStatus
  calendarCode: string
  evidenceHash: string
  certificationStatement: string
  note: string
  /** `fiscalPeriodName`, or `''`. */
  periodName: string
  /** The company's code: the only company label the close DTOs carry. */
  companyName: string
}

export interface CloseRunDetailDTO extends CloseRunDTO {
  tasks: CloseTaskDTO[]
  signoffs: CloseSignoffDTO[]
}

export interface CloseBoardRowDTO extends Omit<WireBoardRow, 'status' | 'blockingReadiness'> {
  status: CloseRunStatus
  periodName: string
  companyName: string
  blockingReadiness: string[]
}

export interface CloseBoardDTO {
  rows: CloseBoardRowDTO[]
  generatedAt: string
}

export interface CloseCalendarDTO extends Omit<WireCalendar, 'scope'> {
  scope: CloseScope
  /** The company's code: the only company label the calendar DTO carries. */
  companyName: string
}

export interface CloseTaskTemplateDTO
  extends Omit<WireTemplate, 'taskKind' | 'readinessKey' | 'actionKey' | 'obligationKind' | 'module' | 'instructions'> {
  taskKind: CloseTaskKind
  readinessKey: string
  actionKey: string
  obligationKind: string
  module: string
  instructions: string
}

export interface CloseCalendarDetailDTO extends CloseCalendarDTO {
  templates: CloseTaskTemplateDTO[]
}

/** The template editor's row: the wire input with the fields the editor always fills made required. */
export interface CloseTaskTemplateInput extends CloseTemplateInput {
  seq: number
  taskKind: CloseTaskKind
  dueOffsetDays: number
  isMandatory: boolean
  requiresEvidence: boolean
  dependsOn: string[]
}

export interface Page<T> {
  items: T[]
  nextCursor: string | null
  hasMore: boolean
}

/* ----------------------------------------------------------- normalisers */

function str(value: string | undefined | null): string {
  return value ?? ''
}

function asSeverity(value: string): CloseSeverity {
  return value === 'blocking' ? 'blocking' : 'advisory'
}

export function toReadinessItem(item: WireReadinessItem): CloseReadinessItemDTO {
  return {
    ...item,
    module: str(item.module),
    severity: asSeverity(item.severity),
    messageKey: str(item.messageKey),
    link: str(item.link),
    detail: item.detail ?? {},
  }
}

function toReadiness(wire: WireReadiness): CloseReadinessDTO {
  return {
    ...wire,
    items: wire.items.map(toReadinessItem),
    periodName: str(wire.fiscalPeriodName),
    evaluatedAt: wire.generatedAt,
  }
}

function numberIn(detail: Record<string, unknown>, key: string): number {
  const value = detail[key]
  return typeof value === 'number' && Number.isFinite(value) ? value : 0
}

function stringIn(record: Record<string, unknown>, key: string): string {
  const value = record[key]
  return typeof value === 'string' ? value : ''
}

function outstandingIn(detail: Record<string, unknown>): CloseObligationItemDTO[] {
  const raw = detail['outstanding']
  if (!Array.isArray(raw)) return []
  const items: CloseObligationItemDTO[] = []
  for (const entry of raw) {
    if (entry === null || typeof entry !== 'object') continue
    const record = entry as Record<string, unknown>
    const docId = record['docId']
    const dueOn = stringIn(record, 'dueOn')
    items.push({
      docType: stringIn(record, 'docType'),
      docId: typeof docId === 'number' ? docId : 0,
      docNo: stringIn(record, 'docNo'),
      status: stringIn(record, 'status'),
      satisfied: false,
      link: stringIn(record, 'link'),
      ...(dueOn === '' ? {} : { dueOn }),
    })
  }
  return items
}

/**
 * A task as the screens read it.
 *
 * The readiness item for a readiness task is rebuilt from the task's own
 * `readiness*` fields. Its severity follows the task's `isMandatory`: a
 * mandatory readiness task is the one a period close refuses on.
 */
export function toTask(wire: WireTask): CloseTaskDTO {
  const detail = wire.computedDetail ?? {}
  const kind = wire.taskKind as CloseTaskKind
  const computed = wire.computedReady
  const errorText = stringIn(detail, 'error')
  const readiness: CloseReadinessItemDTO | null =
    kind === 'readiness' && computed !== null && computed !== undefined
      ? {
          key: str(wire.readinessKey) === '' ? wire.code : str(wire.readinessKey),
          module: str(wire.module),
          severity: wire.isMandatory ? 'blocking' : 'advisory',
          ready: computed,
          blocks: !computed && wire.isMandatory,
          count: wire.readinessCount ?? 0,
          ...(wire.readinessAmount === undefined || wire.readinessAmount === ''
            ? {}
            : { amount: wire.readinessAmount }),
          messageKey: str(wire.readinessMessageKey),
          link: str(wire.readinessLink),
          detail,
          ...(errorText === '' ? {} : { error: errorText }),
        }
      : null
  return {
    ...wire,
    status: wire.status as CloseTaskStatus,
    taskKind: kind,
    module: str(wire.module),
    readinessKey: str(wire.readinessKey),
    actionKey: str(wire.actionKey),
    obligationKind: str(wire.obligationKind),
    blockedBy: wire.blockedBy ?? [],
    dependsOn: wire.dependsOn ?? [],
    acceptedReasonCode: str(wire.acceptedReasonCode),
    acceptedNote: str(wire.acceptedNote),
    resultDocType: str(wire.resultDocType),
    note: str(wire.note),
    computedDetail: detail,
    readiness,
    obligationItems: kind === 'obligation' ? outstandingIn(detail) : [],
    obligationTotal: kind === 'obligation' ? numberIn(detail, 'total') : 0,
    obligationSatisfied: kind === 'obligation' ? numberIn(detail, 'satisfied') : 0,
  }
}

function toRun(wire: WireRun): CloseRunDTO {
  return {
    ...wire,
    status: wire.status as CloseRunStatus,
    calendarCode: str(wire.calendarCode),
    evidenceHash: str(wire.evidenceHash),
    certificationStatement: str(wire.certificationStatement),
    note: str(wire.note),
    periodName: str(wire.fiscalPeriodName),
    companyName: str(wire.companyCode),
  }
}

function toRunDetail(wire: WireRunDetail): CloseRunDetailDTO {
  return {
    ...toRun(wire),
    tasks: (wire.tasks ?? []).map(toTask),
    signoffs: wire.signoffs ?? [],
  }
}

function toBoardRow(wire: WireBoardRow): CloseBoardRowDTO {
  return {
    ...wire,
    status: wire.status as CloseRunStatus,
    periodName: str(wire.fiscalPeriodName),
    companyName: str(wire.companyCode),
    blockingReadiness: wire.blockingReadiness ?? [],
  }
}

function toCalendar(wire: WireCalendar): CloseCalendarDTO {
  return { ...wire, scope: wire.scope as CloseScope, companyName: str(wire.companyCode) }
}

function toTemplate(wire: WireTemplate): CloseTaskTemplateDTO {
  return {
    ...wire,
    taskKind: wire.taskKind as CloseTaskKind,
    readinessKey: str(wire.readinessKey),
    actionKey: str(wire.actionKey),
    obligationKind: str(wire.obligationKind),
    module: str(wire.module),
    instructions: str(wire.instructions),
  }
}

function toCalendarDetail(wire: WireCalendarDetail): CloseCalendarDetailDTO {
  return { ...toCalendar(wire), templates: (wire.templates ?? []).map(toTemplate) }
}

/* ------------------------------------------------------------- tones etc. */

export type Tone = 'success' | 'warning' | 'danger' | 'accent' | 'neutral' | 'info'

/**
 * The colour each of the eight states carries.
 *
 * Colour means state and nothing else (CLAUDE.md §11), so the mapping is stated
 * once and every surface — board, lane, card, rail — reads it here rather than
 * choosing again. `skipped` is neutral rather than green on purpose: a task the
 * plan did not entitle is *not* an accomplishment, and rendering it the same as
 * `completed` would let a controller believe a check ran that never did.
 */
export function taskStatusTone(status: CloseTaskStatus): Tone {
  switch (status) {
    case 'completed':
      return 'success'
    case 'accepted':
      return 'warning'
    case 'ready':
      return 'accent'
    case 'in_progress':
      return 'info'
    case 'blocked':
      return 'danger'
    case 'failed':
      return 'danger'
    case 'skipped':
      return 'neutral'
    default:
      return 'neutral'
  }
}

export function runStatusTone(status: CloseRunStatus): Tone {
  switch (status) {
    case 'certified':
    case 'closed':
      return 'success'
    case 'ready':
      return 'accent'
    case 'in_progress':
      return 'info'
    case 'abandoned':
      return 'neutral'
    default:
      return 'neutral'
  }
}

export function severityTone(severity: CloseSeverity): Tone {
  return severity === 'blocking' ? 'danger' : 'warning'
}

/** §4.3(3): a blocked task may not be completed. There is no force. */
export function isSatisfied(task: Pick<CloseTaskDTO, 'status'>): boolean {
  return SATISFIED_STATUSES.includes(task.status)
}

/**
 * Whether a task's Complete affordance is offered at all.
 *
 * The server is the authority — `CompleteTask` returns `ERR_CLOSE_TASK_BLOCKED`
 * or `ERR_CLOSE_NOT_READY` and those are shown when they arrive — but offering a
 * button that cannot work is how a controller learns to distrust the screen.
 */
export function isCompletable(task: CloseTaskDTO): boolean {
  if (isSatisfied(task)) return false
  if (task.status === 'blocked') return false
  if (task.taskKind === 'readiness' || task.taskKind === 'obligation') {
    return task.computedReady === true
  }
  return true
}

/** A readiness or obligation task that says no is the one `AcceptTask` exists for. */
export function isAcceptable(task: CloseTaskDTO): boolean {
  if (isSatisfied(task)) return false
  if (task.taskKind !== 'readiness' && task.taskKind !== 'obligation') return false
  return task.computedReady !== true
}

/** Names of the predecessors holding a task back, for the "blocked by" line. */
export function unsatisfiedNames(task: CloseTaskDTO, all: readonly CloseTaskDTO[]): string[] {
  const byCode = new Map(all.map((candidate) => [candidate.code, candidate]))
  return task.blockedBy.map((code) => byCode.get(code)?.name ?? code)
}

/**
 * Dependency **lanes**: the longest path from a task with no predecessors.
 *
 * Presentation only. §4.3(7) is explicit that `seq` is presentation and the
 * graph is execution, so laying the graph out by depth is the honest rendering:
 * two tasks in the same lane genuinely cannot block each other. Edges are the
 * server's `dependsOn` **codes**, resolved within the run.
 *
 * A task whose predecessors are missing from `tasks` (a filtered list) lands in
 * lane 0, which is the truthful answer for the set it was given.
 */
export function dependencyLanes(tasks: readonly CloseTaskDTO[]): CloseTaskDTO[][] {
  const byCode = new Map(tasks.map((task) => [task.code, task]))
  const depth = new Map<number, number>()

  function depthOf(task: CloseTaskDTO, seen: Set<number>): number {
    const cached = depth.get(task.id)
    if (cached !== undefined) return cached
    // A cycle cannot survive `SetTaskTemplates` (§4.3(4) refuses it with
    // ERR_CLOSE_TASK_CYCLE), but a layout that loops forever on bad data is a
    // frozen tab, so the walk carries its own visited set.
    if (seen.has(task.id)) return 0
    seen.add(task.id)
    let best = 0
    for (const parentCode of task.dependsOn) {
      const parent = byCode.get(parentCode)
      if (parent === undefined) continue
      best = Math.max(best, depthOf(parent, seen) + 1)
    }
    seen.delete(task.id)
    depth.set(task.id, best)
    return best
  }

  const lanes: CloseTaskDTO[][] = []
  for (const task of tasks) {
    const lane = depthOf(task, new Set())
    while (lanes.length <= lane) lanes.push([])
    lanes[lane]?.push(task)
  }
  for (const lane of lanes) lane.sort((a, b) => a.seq - b.seq || a.id - b.id)
  return lanes
}

/** Counts per state, for the cockpit's legend. Every state appears, zero or not. */
export function statusCounts(tasks: readonly CloseTaskDTO[]): Record<CloseTaskStatus, number> {
  const counts = Object.fromEntries(TASK_STATUSES.map((status) => [status, 0])) as Record<
    CloseTaskStatus,
    number
  >
  for (const task of tasks) counts[task.status] += 1
  return counts
}

/** Blocking readiness answers currently failing on a run, gathered from its tasks. */
export function failingReadiness(tasks: readonly CloseTaskDTO[]): CloseReadinessItemDTO[] {
  return tasks
    .map((task) => task.readiness)
    .filter((item): item is CloseReadinessItemDTO => item !== null && !item.ready)
}

/* ----------------------------------------------------------------- errors */

export const CLOSE_ERROR_CODES = {
  rowVersion: 'ERR_ROW_VERSION',
  sod: 'ERR_SOD_VIOLATION',
} as const

/** Somebody else changed the run or task first: reload, then let the user reapply. */
export function isRowVersionConflict(error: unknown): boolean {
  const problem = toProblemDetails(error)
  return problem?.code === CLOSE_ERROR_CODES.rowVersion
}

/* -------------------------------------------------------------- calendars */

export interface CalendarFilters {
  companyId?: number | null
  scope?: string
  isActive?: boolean | null
}

export async function listCalendars(
  filters: CalendarFilters,
  page: { cursor?: string; limit?: number } = {},
): Promise<Page<CloseCalendarDTO>> {
  const result = await closeCalendarsList(api(), {
    limit: page.limit ?? 50,
    ...(page.cursor !== undefined && page.cursor !== '' ? { cursor: page.cursor } : {}),
    ...(filters.companyId === undefined || filters.companyId === null
      ? {}
      : { companyId: filters.companyId }),
    ...(filters.scope === undefined || filters.scope === '' ? {} : { scope: filters.scope }),
    ...(filters.isActive === undefined || filters.isActive === null
      ? {}
      : { isActive: filters.isActive }),
  })
  return { ...result, items: result.items.map(toCalendar) }
}

export async function listAllCalendars(companyId?: number): Promise<CloseCalendarDTO[]> {
  const items: CloseCalendarDTO[] = []
  let cursor = ''
  for (;;) {
    const result = await closeCalendarsList(api(), {
      limit: MAX_LIMIT,
      ...(companyId === undefined ? {} : { companyId }),
      ...(cursor === '' ? {} : { cursor }),
    })
    items.push(...result.items.map(toCalendar))
    if (!result.hasMore || result.nextCursor === null || result.nextCursor === '') break
    cursor = result.nextCursor
  }
  return items
}

export async function getCalendar(id: number): Promise<CloseCalendarDetailDTO> {
  return toCalendarDetail(await closeCalendarsGet(api(), { id: String(id) }))
}

export async function createCalendar(
  body: CreateCloseCalendarRequest,
  idempotencyKey: string,
): Promise<CloseCalendarDTO> {
  return toCalendar(await closeCalendarsCreate(api(), body, idempotencyKey))
}

export async function updateCalendar(
  id: number,
  body: UpdateCloseCalendarRequest,
): Promise<CloseCalendarDTO> {
  return toCalendar(await closeCalendarsUpdate(api(), { id: String(id) }, body))
}

/** 422 `ERR_CLOSE_TASK_CYCLE` names the codes on the cycle — the editor shows them. */
export async function setTemplates(
  id: number,
  templates: CloseTaskTemplateInput[],
): Promise<CloseCalendarDetailDTO> {
  return toCalendarDetail(await closeCalendarsSetTemplates(api(), { id: String(id) }, { templates }))
}

/** The caller holds one key per submission (`useSubmissionKey`); a wrapper never mints. */
export async function seedDefaultTemplates(
  id: number,
  idempotencyKey: string,
): Promise<CloseCalendarDetailDTO> {
  return toCalendarDetail(await closeCalendarsSeedDefaults(api(), { id: String(id) }, idempotencyKey))
}

/* -------------------------------------------------------------------- runs */

export interface RunFilters {
  companyId?: number | null
  calendarId?: number | null
  fiscalPeriodId?: number | null
  status?: string
}

export async function listRuns(
  filters: RunFilters,
  page: { cursor?: string; limit?: number } = {},
): Promise<Page<CloseRunDTO>> {
  const query: ListCloseRunsQuery = {
    limit: page.limit ?? 50,
    ...(page.cursor !== undefined && page.cursor !== '' ? { cursor: page.cursor } : {}),
    ...(filters.companyId === undefined || filters.companyId === null
      ? {}
      : { companyId: filters.companyId }),
    ...(filters.calendarId === undefined || filters.calendarId === null
      ? {}
      : { calendarId: filters.calendarId }),
    ...(filters.fiscalPeriodId === undefined || filters.fiscalPeriodId === null
      ? {}
      : { fiscalPeriodId: filters.fiscalPeriodId }),
    ...(filters.status === undefined || filters.status === '' ? {} : { status: filters.status }),
  }
  const result = await closeRunsList(api(), query)
  return { ...result, items: result.items.map(toRun) }
}

export async function getRun(id: number): Promise<CloseRunDetailDTO> {
  return toRunDetail(await closeRunsGet(api(), { id: String(id) }))
}

export async function openRun(
  body: OpenCloseRunRequest,
  idempotencyKey: string,
): Promise<CloseRunDetailDTO> {
  return toRunDetail(await closeRunsOpen(api(), body, idempotencyKey))
}

/** Idempotent and side-effect-free outside `close_*` tables (§4.2). */
export async function refreshRun(id: number): Promise<CloseRunDetailDTO> {
  return toRunDetail(await closeRunsRefresh(api(), { id: String(id) }))
}

export async function abandonRun(id: number, body: AbandonCloseRunRequest): Promise<CloseRunDTO> {
  return toRun(await closeRunsAbandon(api(), { id: String(id) }, body))
}

/** Step-up MFA server-side; refuses with `ERR_SOD_VIOLATION` for a sole preparer (§4.7). */
export async function certifyRun(
  id: number,
  statement: string,
  rowVersion: number,
): Promise<CloseRunDetailDTO> {
  return toRunDetail(await closeRunsCertify(api(), { id: String(id) }, { statement, rowVersion }))
}

/**
 * Add an ad-hoc task to an open run (FSD 2.2.8, `closeRunsAddTask`).
 *
 * Dependencies are task **codes** within the run, the same vocabulary the
 * calendar's templates use, so a task can wait on one that is already there.
 */
export async function addTask(id: number, body: AddCloseTaskRequest): Promise<CloseTaskDTO> {
  return toTask(await closeRunsAddTask(api(), { id: String(id) }, body))
}

export async function listTasks(
  runId: number,
  filters: { status?: string; taskKind?: string; overdueOnly?: boolean } = {},
): Promise<Page<CloseTaskDTO>> {
  const result = await closeTasksList(
    api(),
    { id: String(runId) },
    {
      limit: MAX_LIMIT,
      ...(filters.status === undefined || filters.status === '' ? {} : { status: filters.status }),
      ...(filters.taskKind === undefined || filters.taskKind === '' ? {} : { taskKind: filters.taskKind }),
      ...(filters.overdueOnly === true ? { overdue: true } : {}),
    },
  )
  return { ...result, items: result.items.map(toTask) }
}

/* ------------------------------------------------------------------- tasks */

/** Every task write carries the task's `rowVersion`; a stale one is a 409. */
export async function completeTask(id: number, body: CompleteCloseTaskRequest): Promise<CloseTaskDTO> {
  return toTask(await closeTasksComplete(api(), { id: String(id) }, body))
}

export async function acceptTask(id: number, body: AcceptCloseTaskRequest): Promise<CloseTaskDTO> {
  return toTask(await closeTasksAccept(api(), { id: String(id) }, body))
}

export async function reopenTask(id: number, body: ReopenCloseTaskRequest): Promise<CloseTaskDTO> {
  return toTask(await closeTasksReopen(api(), { id: String(id) }, body))
}

export async function assignTask(id: number, body: AssignCloseTaskRequest): Promise<CloseTaskDTO> {
  return toTask(await closeTasksAssign(api(), { id: String(id) }, body))
}

export async function runTaskAction(
  id: number,
  note: string,
  rowVersion: number,
  idempotencyKey: string,
): Promise<CloseTaskDTO> {
  return toTask(
    await closeTasksRunAction(
      api(),
      { id: String(id) },
      note === '' ? { rowVersion } : { note, rowVersion },
      idempotencyKey,
    ),
  )
}

/* ---------------------------------------------------------------- evidence */

export async function listEvidence(runId: number): Promise<CloseEvidenceDTO[]> {
  const items: CloseEvidenceDTO[] = []
  let cursor = ''
  for (;;) {
    const result: PageCloseEvidenceDTO = await closeEvidenceList(
      api(),
      { id: String(runId) },
      { limit: MAX_LIMIT, ...(cursor === '' ? {} : { cursor }) },
    )
    items.push(...result.items)
    if (!result.hasMore || result.nextCursor === null || result.nextCursor === '') break
    cursor = result.nextCursor
  }
  return items
}

/** The caller holds one key per submission (`useSubmissionKey`); a wrapper never mints. */
export function buildEvidencePack(
  runId: number,
  idempotencyKey: string,
): Promise<CloseEvidencePackDTO> {
  return closeRunsBuildEvidencePack(api(), { id: String(runId) }, idempotencyKey)
}

/**
 * The pack itself is a ZIP stream on `GET /close-runs/:id/evidence-pack`. The
 * app authenticates with a bearer token, so a plain link to it would carry no
 * credential and be refused: the bytes are fetched through the authenticated
 * client (`rawRequest` keeps its refresh-and-retry and session-boundary rules)
 * and handed to the browser as a blob.
 */
export async function downloadEvidencePack(runId: number): Promise<void> {
  const response = await rawRequest('GET', `/api/v1/close-runs/${runId}/evidence-pack`)
  const blob = await response.blob()
  const disposition = response.headers.get('Content-Disposition') ?? ''
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition)
  let filename = `close-run-${runId}-evidence-pack.zip`
  const candidate = match?.[1]?.trim()
  if (candidate !== undefined && candidate !== '') {
    try {
      filename = decodeURIComponent(candidate)
    } catch {
      filename = candidate
    }
  }
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}

/* ------------------------------------------------------- board & readiness */

/**
 * The close board. The server filters by company and fiscal year only; the
 * status filter is applied to the rows it returned, which is the same set.
 */
export async function board(filters: {
  companyId?: number | null
  fiscalYear?: number | null
  status?: string
}): Promise<CloseBoardDTO> {
  const result = await closeBoard(api(), {
    ...(filters.companyId === undefined || filters.companyId === null
      ? {}
      : { companyId: filters.companyId }),
    ...(filters.fiscalYear === undefined || filters.fiscalYear === null
      ? {}
      : { fiscalYear: filters.fiscalYear }),
  })
  const rows = result.rows.map(toBoardRow)
  const status = filters.status ?? ''
  return {
    generatedAt: result.generatedAt,
    rows: status === '' ? rows : rows.filter((row) => row.status === status),
  }
}

/** Evaluates without a run, so a controller sees the state before opening one (§6.2). */
export async function readiness(companyId: number, fiscalPeriodId: number): Promise<CloseReadinessDTO> {
  return toReadiness(await closeReadiness(api(), { companyId, fiscalPeriodId }))
}
